import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {QUERIES} from '@github-ui/issue-url-helper/known-queries'
import type {AppPayload} from '@github-ui/issues-shared/app-payload'
import {VALUES} from '@github-ui/issues-shared/values'
import {noop} from '@github-ui/noop'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useLocation} from '@github-ui/react-router'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {createContext, type ReactNode, use, useCallback, useMemo, useRef, useState} from 'react'

import {issueRegexpWithoutDomain, issuesPathRegexp} from '../utils/urls'

type QueryBulkContextType = {
  bulkJobId: string | null
  setBulkJobId: (bulkJobId: string | null) => void
  registerBulkJobSuccess: (cb: (() => void) | null) => void
  triggerBulkJobSuccess: () => void
}

const QueryCurrentViewIdValueContext = createContext(VIEW_IDS.empty)
const QueryCurrentViewIdSetterContext = createContext<(viewId: string) => void>(noop)
const QueryActiveSearchValueContext = createContext('')
const QueryBulkContext = createContext<QueryBulkContextType>({
  bulkJobId: null,
  setBulkJobId: noop,
  registerBulkJobSuccess: noop,
  triggerBulkJobSuccess: noop,
})

const useInitialViewId = () => {
  const {scoped_repository} = useAppPayload<AppPayload>()
  const {pathname} = useLocation()

  if (pathname.match(issueRegexpWithoutDomain)) {
    return scoped_repository ? VIEW_IDS.repository : VIEW_IDS.assignedToMe
  }

  const id = pathname.split('/').pop() || ''
  if (scoped_repository && pathname.match(issuesPathRegexp)) {
    return VIEW_IDS.repository
  }

  return id === 'issues' || !id ? VIEW_IDS.empty : id
}

export function QueryContextProvider({children}: {children: ReactNode}) {
  const {search} = useLocation()
  const queryParam = new URLSearchParams(search).get('q')
  const activeSearchQuery = !queryParam ? QUERIES.defaultRepoLevelOpen : queryParam
  const initialViewId = useInitialViewId()
  const [currentViewId, setCurrentViewId] = useState(initialViewId)
  const [bulkJobId, setBulkJobId] = useLocalStorage<string | null>(VALUES.localStorageKeyBulkUpdateIssues, null)
  const bulkJobSuccessRef = useRef<(() => void) | null>(null)

  const registerBulkJobSuccess = useCallback((cb: (() => void) | null) => {
    bulkJobSuccessRef.current = cb
  }, [])

  const triggerBulkJobSuccess = useCallback(() => {
    bulkJobSuccessRef.current?.()
  }, [])
  const queryBulkValue = useMemo(
    () => ({bulkJobId, setBulkJobId, registerBulkJobSuccess, triggerBulkJobSuccess}),
    [bulkJobId, setBulkJobId, registerBulkJobSuccess, triggerBulkJobSuccess],
  )

  return (
    <QueryCurrentViewIdValueContext value={currentViewId}>
      <QueryCurrentViewIdSetterContext value={setCurrentViewId}>
        <QueryActiveSearchValueContext value={activeSearchQuery}>
          <QueryBulkContext value={queryBulkValue}>{children}</QueryBulkContext>
        </QueryActiveSearchValueContext>
      </QueryCurrentViewIdSetterContext>
    </QueryCurrentViewIdValueContext>
  )
}

export function useCurrentViewId() {
  return use(QueryCurrentViewIdValueContext)
}

export function useSetCurrentViewId() {
  return use(QueryCurrentViewIdSetterContext)
}

export function useActiveSearchQuery() {
  return use(QueryActiveSearchValueContext)
}

export function useQueryBulk() {
  return use(QueryBulkContext)
}
